import React, { FC, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { Loader, Paragraph, Heading, Svg, Flex } from '@wunder-mobility/ui-components';
import {
  useRequestAvailableSubscriptionsQuery,
  useRequestMySubscriptionsQuery,
} from '@/features/subscriptions/services/Subscription.service';
import { useRequestResolvedBranchQuery } from '@/features/branches/services/Branches.service';
import {
  mergeSubscriptions,
  isSwitchable,
  isSwitching,
} from '@/features/subscriptions/utils/subscriptionHelpers';
import SubscriptionTile from './SubscriptionTile';
import styles from '@/features/subscriptions/assets/scss/SubscriptionOverview.module.scss';

const SubscriptionOverview: FC = () => {
  const { data: resolvedBranch } = useRequestResolvedBranchQuery();

  // Fetch available subscriptions for the branch (catalog)
  const {
    data: availableSubscriptions,
    isLoading: isLoadingAvailable,
    error: errorAvailable,
  } = useRequestAvailableSubscriptionsQuery(
    { branchId: resolvedBranch?.id! },
    { skip: !resolvedBranch?.id },
  );

  // Fetch user's owned subscriptions
  const {
    data: mySubscriptions,
    isLoading: isLoadingMySubscriptions,
    error: errorMySubscriptions,
  } = useRequestMySubscriptionsQuery();

  const isLoading = isLoadingAvailable || isLoadingMySubscriptions;
  const error = errorAvailable || errorMySubscriptions;

  const allSubscriptions = useMemo(
    () => mergeSubscriptions(availableSubscriptions || [], mySubscriptions || []),
    [availableSubscriptions, mySubscriptions],
  );

  // The user's switchable (ongoing) subscription — active-renewing or already switching.
  // Only this one carries a valid id to switch from; a cancelled/expired plan is terminal for
  // switching, so the other tiles fall back to a fresh purchase instead.
  const switchableSubscription = useMemo(
    () => (mySubscriptions || []).find(isSwitchable),
    [mySubscriptions],
  );

  // Treat the user as "having an active subscription" (switch, not buy) only when an ongoing
  // plan exists. A cancelled/expired plan frees the tiles to purchase a new one.
  const hasActiveSubscription = !!switchableSubscription;

  // While a switch is pending (SWITCHING), the backend rejects switching from that subscription,
  // so every tile EXCEPT the user's current (switching) plan is locked; the user must cancel the
  // pending switch first. Cancelling clears this automatically.
  const pendingSwitch = useMemo(() => (mySubscriptions || []).find(isSwitching), [mySubscriptions]);

  if (isLoading) {
    return (
      <Flex align="center" justify="center" className={styles['subscription-overview__loading']}>
        <Loader />
      </Flex>
    );
  }

  if (error || !availableSubscriptions) {
    return (
      <Flex direction="column" align="center" gap="md">
        <Svg icon="info" width={60} />
        <Heading size={4}>
          <Trans id="subscriptions.error.title">Unable to load subscriptions</Trans>
        </Heading>
        <Paragraph>
          <Trans id="subscriptions.error.description">
            Please try again later or contact support.
          </Trans>
        </Paragraph>
      </Flex>
    );
  }

  if (allSubscriptions.length === 0) {
    return (
      <Flex direction="column" align="center" className={styles['subscription-overview__empty']}>
        <Flex className={styles['subscription-overview__empty-icon']}>
          <Svg icon="card" width={60} />
        </Flex>
        <Heading size={4} className={styles['subscription-overview__empty-title']}>
          <Trans id="subscriptions.empty.title">No subscriptions available</Trans>
        </Heading>
        <Paragraph className={styles['subscription-overview__empty-description']}>
          <Trans id="subscriptions.empty.description">
            There are currently no subscription plans available. Check back later!
          </Trans>
        </Paragraph>
      </Flex>
    );
  }

  return (
    <Flex direction="column" width="full" gap="xl">
      <Paragraph size={3} marginBottom={32}>
        <Trans id="subscriptions.overview.description">
          Choose a subscription plan that fits your needs. Enjoy exclusive benefits and savings on
          your rides.
        </Trans>
      </Paragraph>

      <Flex gap="lg" wrap width="full" className={styles['subscription-overview__tiles-container']}>
        {allSubscriptions.map((subscription) => (
          <SubscriptionTile
            key={subscription.code}
            subscription={subscription}
            hasActiveSubscription={hasActiveSubscription}
            activeSubscription={switchableSubscription}
            isLockedByPendingSwitch={!!pendingSwitch && subscription.code !== pendingSwitch.code}
          />
        ))}
      </Flex>
    </Flex>
  );
};

export default SubscriptionOverview;
